/**
 * The application shell: sidebar nav, header, and the routed page.
 *
 * Functional spec: `qt_poc/sidebar_qt.py` — same six destinations, same
 * Hebrew labels, same order, plus the theme toggle and logout it carries.
 * The sidebar sits on the RIGHT because the document is RTL; that is the
 * `AppShell` navbar's natural side under `dir="rtl"`, not something to force.
 */
import { useState } from 'react'
import { AppShell, Badge, Box, Burger, Button, Group, Menu, NavLink, ScrollArea, Text, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { NavLink as RouterNavLink, Outlet, useLocation } from 'react-router-dom'

import { ROLE_LABEL_HE, useAuth } from '../auth/AuthContext'
import { ErrorBoundary } from './ErrorBoundary'
import { ChangePasswordModal } from './PasswordModals'
import { ThemeToggle } from './ThemeToggle'

const NAV = [
  { to: '/catalog', label: 'קטלוג פריטים', icon: '📦' },
  { to: '/bom', label: 'BOM פרויקטים', icon: '🧾' },
  { to: '/inventory', label: 'מלאי', icon: '🏷️' },
  { to: '/orders', label: 'הזמנות רכש', icon: '🛒' },
  { to: '/customers', label: 'לקוחות', icon: '👥' },
  { to: '/settings', label: 'הגדרות', icon: '⚙️' },
]

export function Shell() {
  const [opened, { toggle, close }] = useDisclosure()
  const { user, logout } = useAuth()
  const location = useLocation()
  const [changingPassword, setChangingPassword] = useState(false)

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 230, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        {/* `wrap="nowrap"` and the truncating title are load-bearing on a phone.
            The header is a fixed 56px; when its contents wrapped to a second
            line they overflowed downward and sat on top of the page heading and
            its buttons — measured at 390px wide. Nothing may wrap here. */}
        <Group h="100%" px="md" justify="space-between" wrap="nowrap" gap="xs">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            {/* The only route to navigation on a phone, so it gets a real target:
                `sm` measured 28px. */}
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="md"
                    aria-label="תפריט" />
            <Title
              order={4}
              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              מערכת ניהול רכש ו-BOM
            </Title>
          </Group>
          <Group gap="xs" wrap="nowrap">
            {user && (
              // Both the name and the role badge go below `sm`. Keeping the
              // badge squeezed the row until Mantine truncated it to "מ…" and
              // clipped "יציאה" to "יציא" — a header that reports the wrong
              // role is worse than one that reports none, and logout must stay
              // legible. The role is still visible in הגדרות → משתמשים.
              <Group gap={6} wrap="nowrap" visibleFrom="sm">
                <Text size="sm">{user.username}</Text>
                <Badge size="sm" variant="light">{ROLE_LABEL_HE[user.role] ?? user.role}</Badge>
              </Group>
            )}
            <ThemeToggle />
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <Button size="xs" variant="default">חשבון</Button>
              </Menu.Target>
              <Menu.Dropdown>
                {/* Repeated here because the name and role badge are hidden
                    below `sm` — on a phone this menu is the only place the
                    signed-in identity is visible. */}
                <Menu.Label>{user?.username}</Menu.Label>
                <Menu.Item onClick={() => setChangingPassword(true)}>
                  שינוי סיסמה
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item color="red" onClick={() => void logout()}>
                  יציאה
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <ScrollArea>
          {NAV.map((entry) => (
            <NavLink
              key={entry.to}
              component={RouterNavLink}
              to={entry.to}
              label={entry.label}
              leftSection={<span aria-hidden>{entry.icon}</span>}
              // Matched on the path prefix so a nested route (an open BOM,
              // say) keeps its section highlighted.
              active={location.pathname.startsWith(entry.to)}
              onClick={close}
            />
          ))}
        </ScrollArea>
      </AppShell.Navbar>

      {/* `height: 100dvh` (not the class's own `min-height: 100dvh`) plus
          `overflow: hidden` is what stops this from ever being the thing that
          scrolls — see the Box just inside it. Without it, Main grows with
          whatever the route renders and the *browser's own* scrollbar is what
          moves, which is how the operator's catalog screenshot ended up with
          a page scrollbar wrapped around a grid that already had its own:
          the header and the burger scrolled away with everything else, and
          reaching row 50 took two separate scroll gestures in two different
          places. */}
      <AppShell.Main
        style={{ height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {/* The one scroll container for every screen. A route that fits
            (the catalog, once its grid fills the rest instead of a fixed
            45vh) never triggers it; a route that does not (BomDetail's stack
            of cards) scrolls here instead of at the document level, so the
            header and nav stay put. Both halves of `flex` matter: `1` is what
            makes this box actually stretch to fill Main's height instead of
            shrinking to its content (Main's own padding and header offset
            still apply around it, untouched); `minHeight: 0` is what lets it
            then shrink *back below* that content height when the content is
            tall, which a flex item refuses to do on its own (the "min-size:
            auto" default) — without it, Main's `overflow: hidden` above would
            have clipped the overflow instead of this Box scrolling it. */}
        <Box style={{ flex: '1 1 0%', minHeight: 0, overflow: 'auto' }}>
          {/* Keyed on the route so navigating away from a broken screen
              recovers, instead of stranding the user on the error card. */}
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Box>
      </AppShell.Main>

      <ChangePasswordModal
        opened={changingPassword}
        onClose={() => setChangingPassword(false)}
      />
    </AppShell>
  )
}
